import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import styles from './email-maker-ai-text-prompt-box-dropdown.module.scss';
import {useAppSelector} from '@/hooks';
import {setErrorMessage, updateShowAiTextPromptBoxDropdown} from '@Components/email-maker-ai-text-prompt-box-dropdown/email-maker-ai-text-prompt-box-dropdown-reducer';
import type {EmailMakerAiTextPromptBoxProps} from '@Components/email-maker-ai-text-prompt-box/email-maker-ai-text-prompt-box';
import {EmailMakerAiTextPromptBox} from '@Components/email-maker-ai-text-prompt-box/email-maker-ai-text-prompt-box';
import {isComponentLoading} from '@Components/email-maker-ai-text-prompt-box/email-maker-ai-text-prompt-box.reducer';
import {EmailEditorAiPromptBoxVariant} from '@Libraries/ai-email-campaign-content-library';
import {DISABLE_GJS_TOOLBAR_ACTIVE_STYLE, TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';

export type EmailMakerAiTextPromptBoxDropdownProps = EmailMakerAiTextPromptBoxProps;

export function EmailMakerAiTextPromptBoxDropdown({editor, ...props}: EmailMakerAiTextPromptBoxDropdownProps): ReactElement {
  const showDropdown = useAppSelector((state) => {
    return state.emailMakerAiTextPromptBoxDropdown.showAiTextPromptBoxDropdown;
  });

  const selectedTextComponentId = useAppSelector((state) => {
    return state.emailMakerAiTextPromptBoxDropdown.selectedTextComponentId;
  });

  const isLoading = useAppSelector((state) => {
    return isComponentLoading(state, selectedTextComponentId);
  });

  const errorBoxMessage = useAppSelector((state) => {
    return state.emailMakerAiTextPromptBoxDropdown.errorMessage;
  });

  useEffect(() => {
    window.PMW.redux.store.dispatch(updateShowAiTextPromptBoxDropdown(false));
    window.PMW.redux.store.dispatch(setErrorMessage(undefined));
  }, [selectedTextComponentId]);

  useEffect(() => {
    if (isLoading) {
      updateShowDropdown(false);
    }
  }, [isLoading]);

  useEffect(() => {
    document.addEventListener('touchstart', closePopupListener);
    document.addEventListener('mousedown', closePopupListener);

    return (): void => {
      document.removeEventListener('touchstart', closePopupListener);
      document.removeEventListener('mousedown', closePopupListener);
    };
  }, []);

  useEffect(() => {
    const toolbarElement = getToolbarElement();
    if (showDropdown) {
      toolbarElement?.classList.add(DISABLE_GJS_TOOLBAR_ACTIVE_STYLE);
    } else {
      toolbarElement?.classList.remove(DISABLE_GJS_TOOLBAR_ACTIVE_STYLE);
    }
  }, [showDropdown]);

  const getToolbarElement = (): HTMLElement | null => {
    return document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR);
  };

  const setToolbarElementTooltipContent = (tooltipContent: string): void => {
    const toolbarElement = getToolbarElement();
    toolbarElement?.setAttribute('title', tooltipContent);
  };

  const hideTooltip = (): void => {
    setToolbarElementTooltipContent('');
  };

  const resetTooltipContent = (): void => {
    setToolbarElementTooltipContent(window.i18next.t('pmwjs_rewrite_with_ai'));
  };

  const closePopupListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR)?.contains(target)) {
      updateShowDropdown(false);
      window.PMW.redux.store.dispatch(setErrorMessage(undefined));
    }
  };

  const showErrorBox = (): boolean => {
    return errorBoxMessage !== undefined;
  };

  const updateShowDropdown = (state: boolean): void => {
    window.PMW.redux.store.dispatch(updateShowAiTextPromptBoxDropdown(state));
  };

  const getLoaderIcon = (): ReactElement => {
    return (
      <div className={`flex-center ${styles.loader}`}>
        <progress className="icon-circular-loader -xsmall -aidark" />
      </div>
    );
  };

  const getAiIcon = (): ReactElement => {
    return <Icon icon="icon-ai-caption" className={`content-ai`} size={IconSize.SIZE_ICON_16} />;
  };

  const getSelectorForDropdown = (): ReactElement => {
    const icon = getAiIcon();
    return <div className={styles.dropdownSelector}>{icon}</div>;
  };

  const getAiTextPromptBoxPopup = (): ReactElement => {
    return (
      <div onMouseEnter={hideTooltip}>
        <EmailMakerAiTextPromptBox
          {...props}
          editor={editor}
          addHeader={false}
          emailEditorAiPromptBoxVariant={EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR_DROPDOWN}
          showPromptBoxOnRender={!showErrorBox()}
          defaultErrorBoxMessage={errorBoxMessage}
        />
      </div>
    );
  };

  const handleOnClick = (): void => {
    if (showDropdown) {
      return;
    }

    updateShowDropdown(true);
  };

  const handleOnClose = (): void => {
    updateShowDropdown(false);
    if (showErrorBox()) {
      window.PMW.redux.store.dispatch(setErrorMessage(undefined));
    }

    resetTooltipContent();
  };

  const getPromptBoxDropdown = (): ReactElement => {
    return (
      <Dropdown
        open={showDropdown || showErrorBox()}
        popup={getAiTextPromptBoxPopup()}
        selector={getSelectorForDropdown()}
        popupClassName={styles.popupContainer}
        popUpHasCustomWidth
        popUpHasCustomHeight
        topOffset={6}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        shouldFlip
        doesCloseOnOuterClick
        doCloseOnInnerClick={false}
        onClick={handleOnClick}
        onOpen={hideTooltip}
        onClose={handleOnClose}
        leftOffset={12}
      />
    );
  };

  const getUpsellDialogComponent = (): ReactElement => {
    return (
      <div
        onClick={() => {
          window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT);
        }}
        className={styles.premiumIconContainer}
      >
        <Icon icon="icon-crown" type={IconType.PREMIUM} className={`${styles.premiumIcon} color-premium`} size={IconSize.SIZE_ICON_12} />
        {getSelectorForDropdown()}
      </div>
    );
  };

  const getToolbarComponent = (): ReactElement => {
    if (isUserLoggedIn() && isUserPremium()) {
      return isLoading ? getLoaderIcon() : getPromptBoxDropdown();
    }

    return getUpsellDialogComponent();
  };

  return getToolbarComponent();
}
